/**
 * Incarcarea scripturilor de urmarire, in spatele consimtamantului.
 *
 * Pana acum, `config.tracking` (`ga4`, `metaPixel`, `gtm`) era declarat in
 * contract si validat in core, dar **nimic nu il citea in browser**. Piata avea
 * campuri de completat in panou si niciun script randat — config mort, care
 * arata ca o functionalitate.
 *
 * Reguli pe care fisierul le respecta si care nu sunt negociabile:
 *
 *  1. **Nimic nu se incarca inainte de o decizie explicita.** Nu exista „pornim
 *     si oprim daca refuza": scriptul, odata executat, a pus deja cookie-uri si
 *     a trimis deja primul eveniment.
 *  2. **Idempotent.** Fiecare incarcare se face o singura data per pagina.
 *  3. **Retragerea reincarca pagina.** Un script deja executat nu se poate
 *     „descarca" — a pretinde altceva ar fi o minciuna comoda. Reincarcarea e
 *     singura cale prin care browserul ajunge intr-o stare care chiar respecta
 *     refuzul.
 */

import type { PublicMarketConfig } from "@deliveryapp/shared";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
  }
}

/** Ce s-a incarcat deja in pagina asta. */
const loaded = { analytics: false, marketing: false, gtm: false };

function appendScript(src: string, attributes: Record<string, string> = {}): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  for (const [key, value] of Object.entries(attributes)) script.setAttribute(key, value);
  document.head.appendChild(script);
}

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
}

// ---------------------------------------------------------------------------
// Google Analytics 4
// ---------------------------------------------------------------------------

function loadAnalytics(measurementId: string): void {
  if (loaded.analytics || measurementId.length === 0) return;
  loaded.analytics = true;

  ensureDataLayer();
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
  window.gtag?.("js", new Date());
  // `anonymize_ip` nu mai e o optiune in GA4 (adresa e anonimizata din oficiu),
  // dar restrictionarea semnalelor publicitare e — si e ce ne trebuie ca
  // masurarea sa ramana masurare si sa nu devina tacit marketing.
  window.gtag?.("config", measurementId, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

// ---------------------------------------------------------------------------
// Meta Pixel
// ---------------------------------------------------------------------------

function loadMetaPixel(pixelId: string): void {
  if (loaded.marketing || pixelId.length === 0) return;
  loaded.marketing = true;

  // Bucla standard de bootstrap a Meta, scrisa explicit in loc de fragmentul
  // minificat din documentatie: fragmentul contine `if (f.fbq) return`, care pe
  // un portal cu mai multe branduri lasa tacit al doilea pixel neinitializat si
  // trimite evenimentele brandului B catre pixelul brandului A. Aici o piata are
  // un singur pixel prin constructie (o piata = un brand), dar preferam ca
  // motivul sa fie vizibil, nu presupus.
  if (typeof window.fbq !== "function") {
    const queue: unknown[] = [];
    const fbq = function fbqShim(...args: unknown[]) {
      if (typeof fbq.callMethod === "function") fbq.callMethod(...args);
      else queue.push(args);
    } as NonNullable<Window["fbq"]>;
    fbq.queue = queue;
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = fbq;
    appendScript("https://connect.facebook.net/en_US/fbevents.js");
  }

  window.fbq?.("init", pixelId);
  window.fbq?.("track", "PageView");
}

// ---------------------------------------------------------------------------
// Google Tag Manager
// ---------------------------------------------------------------------------

/**
 * GTM e un container: poate incarca orice, inclusiv etichete de marketing. De
 * aceea nu se incarca pe consimtamantul de masurare, ci doar cand ambele scopuri
 * sunt acceptate, si primeste starea prin Consent Mode inainte de a porni.
 * Alternativa — sa il pornim pe „analytics" si sa speram ca administratorul
 * containerului nu a pus in el un pixel — nu e o garantie, e o presupunere.
 */
function loadGtm(containerId: string, decision: { analytics: boolean; marketing: boolean }): void {
  if (loaded.gtm || containerId.length === 0) return;
  if (!decision.analytics || !decision.marketing) return;
  loaded.gtm = true;

  ensureDataLayer();
  window.gtag?.("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  window.gtag?.("consent", "update", {
    ad_storage: decision.marketing ? "granted" : "denied",
    ad_user_data: decision.marketing ? "granted" : "denied",
    ad_personalization: decision.marketing ? "granted" : "denied",
    analytics_storage: decision.analytics ? "granted" : "denied",
  });
  window.dataLayer?.push({ "gtm.start": Date.now(), event: "gtm.js" });
  appendScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);
}

// ---------------------------------------------------------------------------
// Punctul de intrare
// ---------------------------------------------------------------------------

export interface TrackingDecision {
  analytics: boolean;
  marketing: boolean;
}

/**
 * Aplica decizia. Se poate chema de cate ori vrea apelantul: incarcarile sunt
 * idempotente, iar ce e deja in pagina nu se dubleaza.
 */
export function applyTracking(config: PublicMarketConfig, decision: TrackingDecision): void {
  const tracking = config.tracking;
  if (tracking === undefined) return;

  if (decision.analytics && typeof tracking.ga4 === "string") loadAnalytics(tracking.ga4.trim());
  if (decision.marketing && typeof tracking.metaPixel === "string") loadMetaPixel(tracking.metaPixel.trim());
  if (typeof tracking.gtm === "string") loadGtm(tracking.gtm.trim(), decision);
}

/** A fost incarcat ceva in pagina asta? Decide daca retragerea cere reincarcare. */
export function trackingIsLive(): boolean {
  return loaded.analytics || loaded.marketing || loaded.gtm;
}

// ---------------------------------------------------------------------------
// Evenimente
// ---------------------------------------------------------------------------

/**
 * Identificatorul de eveniment, comun intre pixelul din browser si conversia
 * trimisa server-side.
 *
 * Fara el, aceeasi comanda ajunge de doua ori la Meta — o data din browser, o
 * data din core — si raportul arata dublu. Meta deduplica pe perechea
 * (`event_name`, `event_id`), deci id-ul trebuie generat O SINGURA DATA si
 * folosit de amandoua drumurile. De aceea pleaca si spre core, in comanda.
 */
export function newEventId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Cookie-urile pe care le pune pixelul Meta.
 *
 * Pleaca spre core ca sa insoteasca conversia server-side: fara ele, potrivirea
 * se face doar pe e-mail si telefon si e vizibil mai slaba. Se citesc din
 * `document.cookie` fiindca sunt puse de scriptul Meta, nu de aplicatie — daca
 * pixelul nu a rulat (refuz, blocant de reclame), pur si simplu nu exista, si
 * atunci nu se trimite nimic in locul lor.
 */
export function readMetaCookies(): { fbp: string | null; fbc: string | null } {
  const read = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
  };
  return { fbp: read("_fbp"), fbc: read("_fbc") };
}

/**
 * Trimite un eveniment catre ce e incarcat. Ce nu e incarcat e ignorat tacit —
 * si asta e corect: apelantul nu trebuie sa stie ce a consimtit vizitatorul, ci
 * doar sa descrie ce s-a intamplat.
 */
export function trackEvent(
  name: string,
  params: Record<string, unknown> = {},
  eventId?: string,
): void {
  if (loaded.analytics) {
    window.gtag?.("event", name, eventId === undefined ? params : { ...params, event_id: eventId });
  }
  if (loaded.marketing) {
    window.fbq?.("track", name, params, eventId === undefined ? undefined : { eventID: eventId });
  }
}
